import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Location } from '@/reservation/reservation.model';
import { PricePipe } from '@/common/pipes/price.pipe';
import { skip, take } from 'rxjs';
import { ProductApiService } from '../product.api.service';
import { ProductTransitCountry } from '../product.model';
import { ProductService } from '../product.service';

@Component({
	selector: 'app-product-transit-country-distances',
	standalone: true,
	imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatSelectModule, PricePipe],
	templateUrl: './product-transit-country-distances.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				subscriptSizing: 'dynamic',
			},
		},
	],
})
export class ProductTransitCountryDistancesComponent implements OnInit {
	readonly editable = input.required<boolean>();

	readonly transitCountryFees = signal<ProductTransitCountry[]>([]);
	readonly plannedRouteKilometers = signal<number | null>(null);
	readonly countries = signal<Location[]>([]);

	private readonly productService = inject(ProductService);
	private readonly productApiService = inject(ProductApiService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.productApiService
			.getCountries()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				this.countries.set(result.results);
			});

		this.productService.dataLoaded$.pipe(skip(1), take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.transitCountryFees.set(this.productService.data.transitCountryFees ?? []);
			this.plannedRouteKilometers.set(this.productService.data.plannedRouteKilometers ?? null);
		});
	}

	compareById(a: Location, b: Location): boolean {
		return a?.id === b?.id;
	}

	addRow(): void {
		this.transitCountryFees.update((rows) => [
			...rows,
			{
				id: null,
				location: null,
				fee: null,
			},
		]);
		this.syncToService();
	}

	removeRow(index: number): void {
		this.transitCountryFees.update((rows) => rows.filter((_, i) => i !== index));
		this.syncToService();
	}

	syncToService(): void {
		this.productService.data.transitCountryFees = this.transitCountryFees();
		this.productService.data.plannedRouteKilometers = this.plannedRouteKilometers();
	}

	onPlannedRouteKilometersChange(value: number | null): void {
		this.plannedRouteKilometers.set(value);
		this.syncToService();
	}

	onFeeChange(row: ProductTransitCountry, value: number | string | null): void {
		if (value === null || value === '') {
			row.fee = null;
		} else {
			const parsed = Number(value);
			row.fee = Number.isNaN(parsed) ? null : Math.round(parsed * 100);
		}

		this.syncToService();
	}

	onNumberInputWheel(event: WheelEvent): void {
		(event.target as HTMLInputElement).blur();
	}
}
